import React, { useState, useEffect, useRef } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import util from "../../utils/util";
import TicketInventoryService from '../../services/TicketInventoryService';
import FacilityService from '../../services/FacilityService';
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import moment from "moment";
import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";
import JobAllocationReportUtil from '../../utils/JobAllocationReportUtil';
import { saveAs } from 'file-saver';
import search from "../assets/icons/magnifying-glass-solid.svg";
import {
  Input,
  Button,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Switch,
  Progress,
  Tag
} from 'antd';
import {
  TrophyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

// Spare Parts Impact Analysis Component
const SparePartsAnalytics = ({ data, isVisible }) => {
  const [sparePartsMetrics, setSparePartsMetrics] = useState({
    totalWaitingSpares: 0,
    totalSuppliedSpares: 0,
    avgWaitTime: 0,
    impactRatio: 0,
    technicianImpact: []
  });

  useEffect(() => {
    if (data && data.length > 0) {
      calculateSparePartsImpact();
    }
  }, [data]);

  const calculateSparePartsImpact = () => {
    const totalWaitingSpares = data.reduce((sum, tech) => sum + (tech.Onhold_Waiting_Spares_tickets || 0), 0);
    const totalSuppliedSpares = data.reduce((sum, tech) => sum + (tech.Onhold_Supplied_Spares_tickets || 0), 0);
    const totalTickets = data.reduce((sum, tech) => sum + (tech.assigned_total || 0), 0);
    
    const impactRatio = totalTickets > 0 ? ((totalWaitingSpares + totalSuppliedSpares) / totalTickets * 100) : 0;
    
    // Calculate per-technician spare parts impact
    const technicianImpact = data.map(tech => {
      const totalAssigned = tech.assigned_total || 0;
      const waitingSpares = tech.Onhold_Waiting_Spares_tickets || 0;
      const suppliedSpares = tech.Onhold_Supplied_Spares_tickets || 0;
      const sparesTotal = waitingSpares + suppliedSpares;
      
      const sparesImpact = totalAssigned > 0 ? (sparesTotal / totalAssigned * 100) : 0;
      const delayFactor = waitingSpares > 0 ? (waitingSpares / (waitingSpares + suppliedSpares + 0.1) * 100) : 0;
      
      return {
        name: tech.assigned_to_name,
        totalAssigned,
        waitingSpares,
        suppliedSpares,
        sparesTotal,
        sparesImpact: Math.round(sparesImpact * 10) / 10,
        delayFactor: Math.round(delayFactor * 10) / 10,
        efficiency: Math.max(0, 100 - sparesImpact)
      };
    }).sort((a, b) => b.sparesImpact - a.sparesImpact);

    setSparePartsMetrics({
      totalWaitingSpares,
      totalSuppliedSpares,
      avgWaitTime: Math.round((totalWaitingSpares / (totalWaitingSpares + totalSuppliedSpares + 0.1)) * 100 * 10) / 10,
      impactRatio: Math.round(impactRatio * 10) / 10,
      technicianImpact: technicianImpact.slice(0, 10)
    });
  };

  const sparesImpactChart = {
    labels: sparePartsMetrics.technicianImpact ? sparePartsMetrics.technicianImpact.map(t => t.name) : [],
    datasets: [
      {
        label: 'Waiting for Spares',
        data: sparePartsMetrics.technicianImpact ? sparePartsMetrics.technicianImpact.map(t => t.waitingSpares) : [],
        backgroundColor: 'rgba(255, 77, 79, 0.6)',
        borderColor: 'rgba(255, 77, 79, 1)',
        borderWidth: 1
      },
      {
        label: 'Spares Supplied',
        data: sparePartsMetrics.technicianImpact ? sparePartsMetrics.technicianImpact.map(t => t.suppliedSpares) : [],
        backgroundColor: 'rgba(24, 144, 255, 0.6)',
        borderColor: 'rgba(24, 144, 255, 1)',
        borderWidth: 1
      }
    ]
  };

  const sparesDistributionChart = {
    labels: ['Waiting for Spares', 'Spares Supplied', 'No Spare Parts Issues'],
    datasets: [{
      data: data && data.length > 0 ? [
        sparePartsMetrics.totalWaitingSpares || 0,
        sparePartsMetrics.totalSuppliedSpares || 0,
        Math.max(0, data.reduce((sum, tech) => sum + (tech.assigned_total || 0), 0) - (sparePartsMetrics.totalWaitingSpares || 0) - (sparePartsMetrics.totalSuppliedSpares || 0))
      ] : [0, 0, 0],
      backgroundColor: ['#ff4d4f', '#1890ff', '#52c41a'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  if (!isVisible) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Waiting for Spares"
              value={sparePartsMetrics.totalWaitingSpares}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Spares Supplied"
              value={sparePartsMetrics.totalSuppliedSpares}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Spares Impact Ratio"
              value={sparePartsMetrics.impactRatio}
              suffix="%"
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Avg Wait Factor"
              value={sparePartsMetrics.avgWaitTime}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Spare Parts Impact by Technician" size="small">
            <div style={{ height: '300px' }}>
              <Bar 
                data={sparesImpactChart} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: true, text: 'Spare Parts Related Tickets' }
                  },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true }
                  }
                }} 
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Overall Spare Parts Distribution" size="small">
            <div style={{ height: '300px' }}>
              <Doughnut 
                data={sparesDistributionChart} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' },
                    title: { display: true, text: 'Tickets by Spare Parts Status' }
                  }
                }} 
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Technician Spare Parts Impact Analysis" size="small">
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {sparePartsMetrics.technicianImpact && sparePartsMetrics.technicianImpact.length > 0 ? sparePartsMetrics.technicianImpact.map((tech, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index < sparePartsMetrics.technicianImpact.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <div style={{ flex: 1 }}>
                    <strong>{tech.name}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {tech.totalAssigned} total tickets
                    </div>
                  </div>
                  <div style={{ flex: 1, padding: '0 16px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      Spare Parts Impact: {tech.sparesImpact}%
                    </div>
                    <Progress 
                      percent={tech.sparesImpact} 
                      strokeColor={tech.sparesImpact > 30 ? '#ff4d4f' : tech.sparesImpact > 15 ? '#fa8c16' : '#52c41a'}
                      size="small"
                      showInfo={false}
                    />
                  </div>
                  <div style={{ textAlign: 'right', minWidth: '120px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      <span style={{ color: '#ff4d4f' }}>{tech.waitingSpares}</span> / 
                      <span style={{ color: '#1890ff', marginLeft: '4px' }}>{tech.suppliedSpares}</span>
                    </div>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      waiting / supplied
                    </div>
                  </div>
                </div>
              )) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  No spare parts data available
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Performance Analytics Component
const TechnicianPerformanceAnalytics = ({ data, isVisible }) => {
  const [analytics, setAnalytics] = useState({
    topPerformers: [],
    performanceMetrics: {},
    efficiencyRatings: [],
    workloadDistribution: {}
  });

  useEffect(() => {
    if (data && data.length > 0) {
      calculatePerformanceMetrics();
    }
  }, [data]);

  const calculatePerformanceMetrics = () => {
    const technicianMetrics = data.map(technician => {
      const totalAssigned = technician.assigned_total || 0;
      const completed = technician.Completed_tickets || 0;
      const closed = technician.Closed_tickets || 0;
      const open = technician.Open_tickets || 0;
      const onHold = (technician.Onhold_Waiting_Spares_tickets || 0) + (technician.Onhold_Supplied_Spares_tickets || 0);
      
      const completionRate = totalAssigned > 0 ? ((completed + closed) / totalAssigned * 100) : 0;
      const backlogRatio = totalAssigned > 0 ? ((open + onHold) / totalAssigned * 100) : 0;
      const productivityScore = totalAssigned > 0 ? (completed * 2 + closed * 2.5 - onHold * 0.5) / totalAssigned * 20 : 0;
      
      return {
        name: technician.assigned_to_name,
        client: technician.client || 'N/A',
        totalAssigned,
        completed,
        closed,
        open,
        onHold,
        completionRate: Math.round(completionRate * 10) / 10,
        backlogRatio: Math.round(backlogRatio * 10) / 10,
        productivityScore: Math.max(0, Math.round(productivityScore * 10) / 10),
        equipmentJobs: technician.equipment_jobs || 0,
        infrastructureJobs: technician.Infrastructure_Jobs || 0
      };
    });

    const topPerformers = [...technicianMetrics]
      .sort((a, b) => b.productivityScore - a.productivityScore)
      .slice(0, 5);

    const totalTechnicians = technicianMetrics.length;
    const avgCompletionRate = technicianMetrics.reduce((sum, t) => sum + t.completionRate, 0) / totalTechnicians;
    const avgProductivityScore = technicianMetrics.reduce((sum, t) => sum + t.productivityScore, 0) / totalTechnicians;
    const totalTickets = technicianMetrics.reduce((sum, t) => sum + t.totalAssigned, 0);

    setAnalytics({
      topPerformers,
      performanceMetrics: {
        totalTechnicians,
        avgCompletionRate: Math.round(avgCompletionRate * 10) / 10,
        avgProductivityScore: Math.round(avgProductivityScore * 10) / 10,
        totalTickets
      },
      efficiencyRatings: technicianMetrics,
      workloadDistribution: calculateWorkloadDistribution(technicianMetrics)
    });
  };

  const calculateWorkloadDistribution = (metrics) => {
    const workloadRanges = {
      'Low (0-10)': 0,
      'Medium (11-25)': 0,
      'High (26-50)': 0,
      'Very High (50+)': 0
    };

    metrics.forEach(tech => {
      if (tech.totalAssigned <= 10) workloadRanges['Low (0-10)']++;
      else if (tech.totalAssigned <= 25) workloadRanges['Medium (11-25)']++;
      else if (tech.totalAssigned <= 50) workloadRanges['High (26-50)']++;
      else workloadRanges['Very High (50+)']++;
    });

    return workloadRanges;
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#fa8c16';
    return '#ff4d4f';
  };

  const getPerformanceLevel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Improvement';
  };

  const completionRateChart = {
    labels: analytics.efficiencyRatings ? analytics.efficiencyRatings.slice(0, 10).map(t => t.name) : [],
    datasets: [{
      label: 'Completion Rate (%)',
      data: analytics.efficiencyRatings ? analytics.efficiencyRatings.slice(0, 10).map(t => t.completionRate) : [],
      backgroundColor: analytics.efficiencyRatings ? analytics.efficiencyRatings.slice(0, 10).map(t => getPerformanceColor(t.completionRate)) : [],
      borderColor: analytics.efficiencyRatings ? analytics.efficiencyRatings.slice(0, 10).map(t => getPerformanceColor(t.completionRate)) : [],
      borderWidth: 1
    }]
  };

  const workloadChart = {
    labels: analytics.workloadDistribution ? Object.keys(analytics.workloadDistribution) : [],
    datasets: [{
      data: analytics.workloadDistribution ? Object.values(analytics.workloadDistribution) : [],
      backgroundColor: ['#52c41a', '#1890ff', '#faad14', '#ff4d4f'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Technician Performance Metrics'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Workload Distribution'
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div className="performance-analytics" style={{ 
      marginBottom: '20px',
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Total Technicians"
              value={analytics.performanceMetrics.totalTechnicians}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Avg Completion Rate"
              value={analytics.performanceMetrics.avgCompletionRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Avg Productivity Score"
              value={analytics.performanceMetrics.avgProductivityScore}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="Total Tickets"
              value={analytics.performanceMetrics.totalTickets}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} lg={14}>
          <Card title="Completion Rate by Technician" size="small">
            <div style={{ height: '300px' }}>
              <Bar data={completionRateChart} options={chartOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Workload Distribution" size="small">
            <div style={{ height: '300px' }}>
              <Doughnut data={workloadChart} options={doughnutOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Top 5 Performers" size="small">
            {analytics.topPerformers && analytics.topPerformers.length > 0 ? analytics.topPerformers.map((tech, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none'
              }}>
                <div>
                  <strong>{tech.name}</strong>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {tech.totalAssigned} tickets assigned
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Tag color={getPerformanceColor(tech.productivityScore)}>
                    Score: {tech.productivityScore}
                  </Tag>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {tech.completionRate}% completion
                  </div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No performance data available
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Performance Levels" size="small">
            {analytics.efficiencyRatings.slice(0, 8).map((tech, index) => (
              <div key={index} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>{tech.name}</span>
                  <span style={{ fontSize: '12px', color: getPerformanceColor(tech.completionRate) }}>
                    {getPerformanceLevel(tech.completionRate)}
                  </span>
                </div>
                <Progress 
                  percent={tech.completionRate} 
                  strokeColor={getPerformanceColor(tech.completionRate)}
                  size="small"
                  format={(percent) => `${percent}%`}
                />
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const JobAllocation = () => {
  const [clients, setClients] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [result, setResult] = useState({ data: [], page: {} });
  const [result2, setResult2] = useState({ data: [], page: {} });
  const [showPerformanceAnalytics, setShowPerformanceAnalytics] = useState(true);
  const [showSparePartsAnalytics, setShowSparePartsAnalytics] = useState(false);
  const sdataRef = useRef({ p: 1, ps: 25 });
  const isNational = util.isNational();

  const ticketStatusNames = [
    { id: 1, name: 'Open' },
    { id: 2, name: 'On Hold - Awaiting Spares' },
    { id: 3, name: 'On Hold - Spares Supplied' },
    { id: 4, name: 'Completed' },
    { id: 5, name: 'Closed' }
  ];

  const list1 = (p, ps, getAssignedToUsers) => {
    const ids = new URLSearchParams(window.location.search).get('ids');
    sdataRef.current.p = p || 1;
    sdataRef.current.ps = ps || sdataRef.current.ps;
    sdataRef.current.ids = ids || '';
    util.showLoader();

    TicketInventoryService.lists1(sdataRef.current, getAssignedToUsers).then(({ data }) => {
      setResult({ ...data.result });
      if (data.assigned_users) {
        setAssignedUsers(data.assigned_users);
      }
    }).catch(e => {
      message.error(e.message);
    }).finally(() => {
      util.hideLoader();
    });

    TicketInventoryService.lists11(sdataRef.current, getAssignedToUsers).then(({ data }) => {
      setResult2({ ...data.result });
      if (data.assigned_users) {
        setAssignedUsers(data.assigned_users);
      }
    }).catch(e => {
      message.error(e.message);
    }).finally(() => {
      util.hideLoader();
    });
  };

  useEffect(() => {
    list1(0, 0, 'getAssignedToUsers');

    FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
      let allClients = res.data.result.data.map(v => ({ id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me }));
      setClients(allClients);
    });

    return () => { message.destroy(); };
  }, []);

  const downloadPdf = () => {
    JobAllocationReportUtil.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);  
  };

  const download = () => {
    sdataRef.current.p = 1;
    sdataRef.current.ps = 0;
    util.showLoader();
    TicketInventoryService.lists1(sdataRef.current).then(({ data }) => {
      let header = {
        assigned_to_name: 'TECHNICIANS',
        assigned_total: 'Jobs Allocated',
        equipment_jobs: 'EQUIPMENTS_ASSIGNED',
        Infrastructure_Jobs: 'INFRASTRUCTURE_ASSIGNED',
        Open_tickets: 'Open_tickets',
        Onhold_Waiting_Spares_tickets: 'OnHold_Waiting_Spares_tickets',
        Onhold_Supplied_Spares_tickets: 'OnHold_Supplied_Spares_tickets',
        Completed_tickets: 'Completed_tickets',
        Closed_tickets: 'Closed_tickets',
      };

      let blob = util.convertToCSVBlob(data.result.data, header);
      saveAs(blob, "job allocation-summary.csv");

    }).catch(e => {
      message.error(e.message);
    }).finally(() => {
      util.hideLoader();
    });
  };

  const labels = result.data.map((v) => (isNational === 1 ? v.client : v.assigned_to_name));
  const data = {
    labels,
    datasets: [
      {
        label: 'Open Tickets',
        data: result.data.map((v) => v.Open_tickets),
        backgroundColor: 'rgba(38, 185, 63, 0.6)',
      },
      {
        label: 'Onhold Waiting Spares Tickets',
        data: result.data.map((v) => v.Onhold_Waiting_Spares_tickets),
        backgroundColor: 'rgba(250, 215, 14, 0.6)',
      },
      {
        label: 'Onhold Supplied Spares Tickets',
        data: result.data.map((v) => v.Onhold_Supplied_Spares_tickets),
        backgroundColor: 'rgba(11, 60, 165, 0.6)',
      },
      {
        label: 'Completed Tickets',
        data: result.data.map((v) => v.Completed_tickets),
        backgroundColor: 'rgba(216, 14, 155, 0.8)',
      },
      {
        label: 'Closed Tickets',
        data: result.data.map((v) => v.Closed_tickets),
        backgroundColor: 'rgba(228, 28, 21, 0.6)',
      },
    ],
  };

  const options = {
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'TECHNICIANS',
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'TOTAL ALLOCATIONS',
        },
      },
    },
  };

  const blockStyle = {
    backgroundColor: 'rgba(38, 185, 63, 0.6)',
    border: '1px solid #ccc',
    padding: '10px',
    flex: '1 1 100px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  };
  const blockStyle2 = {
    backgroundColor: 'rgba(250, 215, 14, 0.6)',
    border: '1px solid #ccc',
    padding: '10px',
    flex: '1 1 100px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  };
  const blockStyle3 = {
    backgroundColor: 'rgba(228, 28, 21, 0.6)',
    border: '1px solid #ccc',
    padding: '10px',
    flex: '1 1 100px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  };
  const blockStyle4 = {
    backgroundColor: 'rgba(11, 60, 165, 0.6)',
    border: '1px solid #ccc',
    padding: '10px',
    flex: '1 1 100px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  };
  const blockStyle5 = {
    backgroundColor: 'rgba(216, 14, 155, 0.8)',
    border: '1px solid #ccc',
    padding: '10px',
    flex: '1 1 100px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  };

  const containerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  };

  return (
    <div className='page-content'>
      <div className="page-head uc d-flex">
        <div className="my-auto">
          <h2>Job Allocation Report & Summary</h2>
        </div>
        <div className="my-auto ml-auto">
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <div className="d-flex align-items-center">
              <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                <TrophyOutlined /> Performance
              </span>
              <Switch 
                checked={showPerformanceAnalytics} 
                onChange={setShowPerformanceAnalytics}
                size="small"
              />
            </div>
            <div className="d-flex align-items-center">
              <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                <ExclamationCircleOutlined /> Spare Parts
              </span>
              <Switch 
                checked={showSparePartsAnalytics} 
                onChange={setShowSparePartsAnalytics}
                size="small"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="page-pad" style={{ backgroundColor: 'rgba(128, 0, 0, 0.1)' }}>
        {showPerformanceAnalytics && (
          <TechnicianPerformanceAnalytics 
            data={result.data} 
            isVisible={showPerformanceAnalytics}
          />
        )}

        {showSparePartsAnalytics && (
          <Card 
            title={
              <span>
                <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                Spare Parts Impact Analysis
              </span>
            } 
            style={{ marginBottom: '20px' }}
            size="small"
          >
            <SparePartsAnalytics 
              data={result.data} 
              isVisible={showSparePartsAnalytics}
            />
          </Card>
        )}

        <div style={containerStyle}>
          {result2.data.map((i, v) => (
            <div key={v} style={blockStyle}>
              <p>OPEN ASSIGNED</p>
              {i.Open_tickets}
            </div>
          ))}
          {result2.data.map((i, v) => (
            <div key={v} style={blockStyle2}>
              <p>WAITING SPARES</p>
              {i.Onhold_Waiting_Spares_tickets}
            </div>
          ))}
          {result2.data.map((i, v) => (
            <div key={v} style={blockStyle4}>
              <p>SPARES SUPPLIED</p>
              {i.Onhold_Supplied_Spares_tickets}
            </div>
          ))}
          {result2.data.map((i, v) => (
            <div key={v} style={blockStyle5}>
              <p>COMPLETED</p>
              {i.Completed_tickets}
            </div>
          ))}
          {result2.data.map((i, v) => (
            <div key={v} style={blockStyle3}>
              <p>CLOSED</p>
              {i.Closed_tickets}
            </div>
          ))}
        </div>

        {result.data.length > 0 &&
          <div className="text-secondary mb8">
            Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
          </div>
        }

        <div className="d-flex tbl-search-head">
          <div className="my-auto flex-grow-1 pr10">
            <SearchForm
              dataRef={sdataRef}
              onSearch={list1}
              clients={clients}
              ticketStatusNames={ticketStatusNames}
              assignedUsers={assignedUsers}
              isNational={isNational}
            />
          </div>

          <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
            &nbsp;&nbsp;
            <button 
              type="button" 
              onClick={download} 
              style={{
                backgroundColor: 'green', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '10px', 
                marginRight: '10px'
              }}
            >
              <img src={excel} alt="download" height={15} /> Export Excel
            </button>
            
            &nbsp;&nbsp;
            <button 
              type="button" 
              onClick={downloadPdf} 
              style={{
                backgroundColor: 'red', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '10px', 
                marginRight: '10px'
              }}
            >
              <img src={acrobat} alt="download" height={15} /> Export PDF
            </button>
          </div>
        </div>

        <div className="chart-container" style={{ height: '400px', width: '100%', border: '2px solid black', padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Bar data={data} options={options} />
        </div>

        <If cond={result.data.length}>
          <div className="table-responsive">
            <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
              <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                <tr>
                  {isNational === 1 && <th className="w100">Facility</th>}
                  <th className="w150">Technicians</th>
                  <th className="w100">Tickets Assigned Total</th>
                  <th className="w100">Equipment_Tickets</th>
                  <th className="w100">Infrastructure_Tickets</th>
                  <th className="w100">Open_Tickets</th>
                  <th className="w100">Onhold_Waiting_Spares_Tickets</th>
                  <th className="w100">Onhold_Supplied_Spares_Tickets</th>
                  <th className="w100">Completed_Tickets</th>
                  <th className="w100">Closed_Tickets</th>
                </tr>
              </thead>
              <tbody className="table-text-top font-sm">
                {result.data.map((v, i) => (
                  <tr key={i}>
                    {isNational === 1 && <td>{v.client}</td>}
                    <td>{v.assigned_to_name}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'black', fontWeight: 700 }}>{v.assigned_total}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'brown', fontWeight: 700 }}>{v.equipment_jobs}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.Infrastructure_Jobs}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.Open_tickets}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.Onhold_Waiting_Spares_tickets}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'blue', fontWeight: 700 }}>{v.Onhold_Supplied_Spares_tickets}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'purple', fontWeight: 700 }}>{v.Completed_tickets}</td>
                    <td className="nowrap uc" style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.Closed_tickets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex tbl-foot-bx">
            <AntdPaging
              onChange={list1}
              total={result.page.total_records}
              current={result.page.cur_page}
              pageSize={sdataRef.current.ps}
              showSizeChanger
            />
          </div>
        </If>
        <If cond={!result.data.length}>
          <div className="no-rec">No record found</div>
        </If>
      </div>
    </div>
  );
};

const SearchForm = (props) => {
  let { dataRef, onSearch, clients, ticketStatusNames, isNational } = props;
  let [data, setData] = useState({ ...dataRef.current });
  const [assignedUsers, setAssignedUsers] = useState([]);

  const handleChange = (v, k) => {
    data[k] = v;
    if (k === 'client_id') {
      data.assigned_to = null;
    }
    setData({ ...data });
  };

  useEffect(() => {
    dataRef.current = { ...data };
  }, [data]);

  useEffect(() => {
    let ob = clients.find(v => v.is_me);
    setData({ ...data, client_id: ob?.id });
  }, [clients]);

  useEffect(() => {
    setAssignedUsers(props.assignedUsers);
  }, [props.assignedUsers]);

  useEffect(() => {
    setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
  }, [dataRef.current.p, dataRef.current.ps]);

  return (
    <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
      <div className="d-flex">
        {isNational === 1 &&
          <div className="w150 mr5">
            <div className="text-secondary fs11 mb2">Facility</div>
            <AntdSelect
              placeholder="All Facilities"
              allowClear
              showSearch
              options={clients}
              value={data.client_id}
              onChange={v => {
                data.item_id = null;
                handleChange(v, 'client_id');
              }}
            />
          </div>
        }

        <div className="w250 mr5">
          <div className="text-secondary fs11 mb2">Assigned To</div>
          <AntdSelect
            placeholder="Assigned To (All)"
            showSearch
            allowClear
            options={assignedUsers.filter(v => v.client_id === data.client_id)}
            value={data.assigned_to}
            onChange={v => handleChange(v, 'assigned_to')}
          />
        </div>

        <div className="w200 mr5">
          <div className="text-secondary fs11 mb2">Type</div>
          <AntdSelect
            placeholder="Type"
            allowClear
            options={[{ value: 'Med/NonMed', label: 'Med/NonMed' }, { value: 'Infrastructure', label: 'Infrastructure' }]}
            value={data.type}
            onChange={v => handleChange(v, 'type')}
          />
        </div>

        <div className="w250 mr5">
          <div className="text-secondary fs11 mb2">Status</div>
          <AntdSelect
            placeholder="Status (All)"
            allowClear
            showSearch
            options={ticketStatusNames}
            value={data.status_id}
            onChange={v => handleChange(v, 'status_id')}
          />
        </div>

        <div className="w170 mr5">
          <div className="text-secondary fs11 mb2">From Date</div>
          <AntdDatepicker
            placeholder="From Date"
            allowClear
            value={data.from_date}
            onChange={v => {
              if (data.to_date && moment(v) > moment(data.to_date)) {
                message.error("From date should be less than or equal to To Date");
                v = null;
              }
              handleChange(v, 'from_date');
            }}
          />
        </div>

        <div className="w170 mr5">
          <div className="text-secondary fs11 mb2">To Date</div>
          <AntdDatepicker
            placeholder="To Date"
            allowClear
            value={data.to_date}
            onChange={v => {
              if (data.from_date && moment(v) < moment(data.from_date)) {
                message.error("To date should be greater than or equal to From Date");
                v = null;
              }
              handleChange(v, 'to_date');
            }}
          />
        </div>
        <div className="pt15">
          <Button className="mt3" type="primary" onClick={() => onSearch()}>
            <img src={search} alt='' height={15}/>
          </Button>
        </div>
      </div>
    </form>
  );
};

export default JobAllocation;